import React, { useEffect, useState } from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Stack,
  Box,
  Tooltip,
  Image,
  useToast,
  FormErrorMessage,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, MinusIcon } from '@chakra-ui/icons';
import { Controller, useForm } from 'react-hook-form';
import axiosInstance from '../../../services/axios';
import ReactSelect from 'react-select';
import CustomSelect from '../../../utils/CustomSelect';

const UpdateTrackModal = ({ track, onClose }) => {
  const { register, handleSubmit, setValue, formState: { errors }, control } = useForm();
  const [isOpen, setIsOpen] = useState(false);
  const [imageloading, setImageLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(track.image_url);
  const [streaming_platforms, setStreamingPlatforms] = useState(track.streaming_platforms);
  const [selectedGenres, setSelectedGenres] = useState(
    track.genres?.map(genre => ({ value: genre, label: genre })) || []
  );
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackFile, setTrackFile] = useState(null)
  const [trackUrl, setTrackUrl] = useState(track.trackUrl)
  const toast = useToast();

  const genres = [
    'Afrobeats', 'AfroRave', 'AfroFusion', 'EmoAfrobeats', 'Street-Hop',
    'Pon Pon Music', 'AfroLife', 'Highlife', 'Kuduro', 'Afrocongo',
    'Gqom', 'Azonto', 'Rock', 'Hard Rock', 'Soft Rock', 'Progressive Rock',
    'Punk Rock', 'Alternative Rock', 'Jazz', 'Bebop', 'Swing', 'Fusion',
    'Free Jazz', 'Smooth Jazz', 'Classical', 'Baroque', 'Romantic',
    'Modern', 'Classical Period', 'Contemporary Classical', 'Hip Hop',
    'Rap', 'Trap', 'Gangsta Rap', 'Conscious Hip Hop', 'East Coast Hip Hop',
    'West Coast Hip Hop', 'Electronic', 'House', 'Techno', 'Dubstep',
    'Trance', 'Drum and Bass', 'Ambient', 'Pop', 'Teen Pop', 'Dance Pop',
    'Synth-pop', 'Electropop', 'Pop Rock', 'R&B', 'Contemporary R&B',
    'Soul', 'Funk', 'Disco', 'New Jack Swing', 'Country', 'Bluegrass',
    'Honky Tonk', 'Outlaw Country', 'Country Pop', 'Alternative Country',
    'Reggae', 'Roots Reggae', 'Dub', 'Dancehall', 'Ska', 'Rocksteady'
  ].map(genre => ({ value: genre, label: genre }));




  const handleGenreChange = (selectedOptions) => {
    setSelectedGenres(selectedOptions || []);
    setValue('genres', selectedOptions?.map(option => option.value));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setImageFile(file);
    setImageLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axiosInstance.post(`/music/upload/file?artist=${track.artist_name}`, formData);
      setImageUrl(response.data);

      toast({
        title: 'Image Uploaded Successfully',
        status: 'success',
        isClosable: true,
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Error Uploading Image',
        description: error.message,
        status: 'error',
        isClosable: true,
        duration: 3000,
      });
    } finally {
      setImageLoading(false);
    }
  };

  const handleImageDelete = () => {
    setImageUrl(track.image_url);
    setImageFile("")
    toast({
      title: 'Image Deleted',
      status: 'info',
      isClosable: true,
      duration: 3000,
    });
    document.getElementById('file-upload').value = null;

  };

  const handleAddPlatform = () => {
    setStreamingPlatforms(
      [...streaming_platforms, { platform: '', id: '' }]
    );
  };

  const handleRemovePlatform = (index) => {
    setStreamingPlatforms(
      streaming_platforms.filter((_, idx) => idx !== index)
    );
  };

  const handlePlatformChange = (index, field, value) => {
    setStreamingPlatforms(

      streaming_platforms.map((platform, idx) =>
        idx === index ? { ...platform, [field]: value } : platform
      )
    );
  };

  const handleTrackUpload = async (file) => {
    if (!file) return;
    setTrackFile(file);
    setTrackLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axiosInstance.post(`/music/upload/track/s3?bucket=beatstake-stream&artist=${track.artist_name}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTrackUrl(response.data);

      toast({
        title: 'Track Uploaded Successfully',
        status: 'success',
        isClosable: true,
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Error Uploading Track',
        description: error.message,
        status: 'error',
        isClosable: true,
        duration: 3000,
      });
    } finally {
      setTrackLoading(false);
    }
  };

  useEffect(() => {
    setValue("genres", track.genres);
    setTrackUrl(track.track_url);
  }, [setValue, track.genres, track.track_url]);

  const handleTrackDelete = () => {
    setTrackUrl("")
    setTrackFile(""); // Clear selected file name
    toast({
      title: 'Track Deleted',
      status: 'info',
      isClosable: true,
      duration: 3000,
    });
    document.getElementById('track-upload').value = null;

  };


  const handleUpdateTrack = async (trackId, newDetails) => {
    try {
      await axiosInstance.put(`/music/update_track/${trackId}`, newDetails);
      toast({
        title: 'Track Updated',
        status: 'success',
        isClosable: true,
      });

      setIsOpen(false)
      onClose();

    } catch (error) {
      console.error('Error updating track:', error);
      toast({
        title: 'Error Updating Track',
        description: 'Failed to update track. Please try again.',
        status: 'error',
        isClosable: true,
      });
    }
  };


  const onSubmit = async (data) => {
    setValue("genres", selectedGenres.map(genre => genre.value));
    console.log(data)
    const formData = {
      ...data.updateTrackDetails,
      genres: data.genres,
      streaming_platforms,
      imageUrl,
      trackUrl
    };
    formData.release_date = new Date(formData.release_date).toISOString();
    console.log(formData)
    handleUpdateTrack(track.track_id, formData);

  };

  const onOpen = () => setIsOpen(true);
  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  return (
    <>
      <Tooltip label='Edit' fontSize='md'>
        <IconButton
          icon={<EditIcon />}
          colorScheme="primary"
          size="sm"
          onClick={onOpen}
          aria-label={`Update ${track.track_name}`}
          mr={2}
        />
      </Tooltip>

      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Track</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" as="form" onSubmit={handleSubmit(onSubmit)}>
              <FormControl my={3} isInvalid={errors.updateTrackDetails?.artist_name}>
                <FormLabel>Artist Name</FormLabel>
                <Input
                  type="text"
                  {...register('updateTrackDetails.artist_name', { required: true })}
                  defaultValue={track.artist_name}
                  variant="light"
                  size="sm"
                />
                <FormErrorMessage>{errors.updateTrackDetails?.artist_name && "Artist name is required"}</FormErrorMessage>
              </FormControl>
              <FormControl my={3} isInvalid={errors.updateTrackDetails?.track_name}>
                <FormLabel>Track Name</FormLabel>
                <Input
                  type="text"
                  {...register('updateTrackDetails.track_name', { required: true })}
                  defaultValue={track.track_name}
                  variant="light"
                  size="sm"
                />
                <FormErrorMessage>{errors.updateTrackDetails?.track_name && "Track name is required"}</FormErrorMessage>
              </FormControl>
              <FormControl my={3} isInvalid={errors.updateTrackDetails?.album_name}>
                <FormLabel>Album Name</FormLabel>
                <Input
                  type="text"
                  {...register('updateTrackDetails.album_name', { required: true })}
                  defaultValue={track.album_name}
                  variant="light"
                  size="sm"
                />
                <FormErrorMessage>{errors.updateTrackDetails?.album_name && "Album name is required"}</FormErrorMessage>
              </FormControl>
              <FormControl my={3} isInvalid={!!errors.updateTrackDetails?.album_type}>
                <FormLabel>Album Type</FormLabel>

                <Controller
                  name="updateTrackDetails.album_type"
                  control={control}
                  rules={{ required: true }}
                  defaultValue={track.album_type}
                  render={({ field }) => (
                    <CustomSelect
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select album type"
                      width="100%"
                      variant="light"
                      size="sm"
                    >
                      <option value="single">Single</option>
                      <option value="album">Album</option>
                      <option value="ep">EP</option>
                    </CustomSelect>
                  )}
                />

                <FormErrorMessage>
                  {errors.updateTrackDetails?.album_type && "Album type is required"}
                </FormErrorMessage>
              </FormControl>
              <Image src={imageUrl} alt="Track Artwork" width="200px" />
              <FormControl>
                <Input
                  type="file"
                  display="none"
                  id='file-upload'
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                  accept="image/*"
                />
                <Text>{imageFile?.name}</Text>
                <Button mr={3} as="label" htmlFor="file-upload" variant="brand" my={3} bg={imageUrl && imageFile && !imageloading ? "green.300" : "dark.900"}>
                  {imageloading ? <Spinner size="sm" /> : imageUrl && imageFile ? '✔' : "change image"}
                </Button>
                {imageUrl && !imageloading && imageFile && (
                  <Button
                    variant="outline"
                    colorScheme="red"
                    onClick={handleImageDelete}
                  >
                    Delete
                  </Button>
                )}
              </FormControl>
              <FormControl my={3}>
                <FormLabel>Upload Track (MP3 or WAV)</FormLabel>
                <Input
                  type="file"
                  display="none"
                  accept=".mp3,.wav"
                  id='track-upload'
                  onChange={(e) => handleTrackUpload(e.target.files[0])}
                />
                <Text>{trackUrl}</Text>
                <Button as="label" htmlFor="track-upload" variant="brand" bg={trackUrl && trackFile && !trackLoading ? "green.300" : "dark.900"}>
                  {trackLoading ? <Spinner size="sm" /> : trackUrl ? '✔' : 'change track'}
                </Button>
                {trackUrl && !trackLoading && (
                  <Button
                    variant="outline"
                    colorScheme="red"
                    onClick={handleTrackDelete}
                    ml={2}
                  >
                    Delete
                  </Button>
                )}

                <Text>{trackFile?.name}</Text>
              </FormControl>
              <FormControl my={3} isInvalid={errors.genres}>
                <FormLabel>Genres</FormLabel>
                <ReactSelect
                  {...register('genres', { required: false })}
                  options={genres}
                  isMulti
                  defaultValue={selectedGenres}
                  value={selectedGenres}
                  onChange={handleGenreChange}
                />
                <FormErrorMessage>{errors.genres && "At least one genre is required"}</FormErrorMessage>
              </FormControl>

              <FormControl my={3} isInvalid={errors.updateTrackDetails?.release_date}>
                <FormLabel>Release Date</FormLabel>
                <Input
                  type="date"
                  {...register('updateTrackDetails.release_date', { required: true })}
                  defaultValue={track.release_date.split("T")[0]}
                  variant="light"
                  size="sm"
                />
                <FormErrorMessage>{errors.updateTrackDetails?.release_date && "Release date is required"}</FormErrorMessage>
              </FormControl>

              <FormControl my={3} isInvalid={errors.updateTrackDetails?.share_limit}>
                <FormLabel>Share Limit</FormLabel>
                <Input
                  type="number"
                  {...register('updateTrackDetails.share_limit', { required: true })}
                  defaultValue={track.share_limit}
                  variant="light"
                  size="sm"
                />
                <FormErrorMessage>{errors.updateTrackDetails?.share_limit && "Share limit is required"}</FormErrorMessage>
              </FormControl>
              {/* <FormControl my={3}>
                <FormLabel>Disabled</FormLabel>
                <Switch
                  isChecked={track.disabled}
                  {...register('updateTrackDetails.disabled')}
                  onChange={(e) => setValue('updateTrackDetails.disabled', e.target.checked)}
                  size="sm"
                />
              </FormControl>
              <FormControl my={3}>
                <FormLabel>Featured</FormLabel>
                <Switch
                  isChecked={track.featured}
                  {...register('updateTrackDetails.featured')}
                  onChange={(e) => setValue('updateTrackDetails.featured', e.target.checked)}
                  size="sm"
                />
              </FormControl> */}
              <FormControl>
                <FormLabel>Streaming Platforms</FormLabel>
                <Stack spacing={2}>
                  {streaming_platforms.map((pf, index) => (
                    <Box key={index} display="flex" alignItems="center">
                      <CustomSelect
                        flex="1"
                        width="auto"
                        value={pf.platform}
                        onChange={(val) => handlePlatformChange(index, 'platform', val)}
                        placeholder="Select Platform"
                      >
                        <option value="apple">Apple Music</option>
                        <option value="spotify">Spotify</option>
                      </CustomSelect>
                      <Input
                        flex="3"
                        ml={2}
                        value={pf.id}
                        onChange={(e) => handlePlatformChange(index, 'id', e.target.value)}
                        placeholder="Track ID"
                        variant="light"
                      />
                      {index > 0 && (
                        <IconButton
                          ml={2}
                          icon={<MinusIcon />}
                          onClick={() => handleRemovePlatform(index)}
                          colorScheme="red"
                        />
                      )}
                    </Box>
                  ))}
                  <Button onClick={handleAddPlatform} leftIcon={<AddIcon />} color="dark.900" borderColor="dark.900" variant="solid">
                    Add Platform
                  </Button>
                </Stack>
              </FormControl>

              <ModalFooter>
                <Button colorScheme="primary" type="submit" mr={3}>
                  Update
                </Button>
                <Button onClick={handleClose}>Cancel</Button>
              </ModalFooter>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UpdateTrackModal;
